import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridSearchIcon } from '@mui/x-data-grid';
import CommonTable from 'components/common/CommonTable'; // Adjust the path as per your setup
import { Box, Button, TextField, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { Icon } from '@iconify/react';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import { axios } from 'utils/axios';
import DriverFiltersDrawer from 'components/_dashboard/driver/DriverFilterDrawer';
import { CheckCircle, Cancel, EmojiTransportation } from '@mui/icons-material';
import useSettings from 'hooks/useSettings';
import { Container } from '@mui/material';
import CustomPaper from 'components/common/CustomPaper';
import { InputAdornment } from '@mui/material';

const defaultFilters = {
  search: '',
  pageSize: 10,
  CompanyID: '',
  dateFrom: '',
  dateTo: '',
  name: '',
  client: '',
  haulier: '',
  DefaultVehicleIds: '',
  inducted: ''
};

const Drivers = () => {
  const navigate = useNavigate();
  const { themeStretch } = useSettings();

  const [drivers, setDrivers] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(defaultFilters);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const [totalRows, setTotalRows] = useState(0);

  const fetchDrivers = async (isClear?: boolean, searchFilters?: any) => {
    setLoading(true);
    try {
      // Use default filters if isClear is true, otherwise use current filters
      const appliedFilters: any = isClear
        ? defaultFilters
        : searchFilters
        ? searchFilters
        : filters;

      const query = new URLSearchParams({
        page: page.toString(),
        ...Object.entries(appliedFilters)
          .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      }).toString();

      const response = await axios.get(`/rest/api/drivers?${query}`);
      const data = response.data.data || [];
      setDrivers(data);
      setTotalRows(response.data.pageMeta.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [page, pageSize]);

  const columns = [
    { field: 'FullName', headerName: 'Name', flex: 1, minWidth: 200 },
    {
      field: 'VehicleRegistrationPlate',
      headerName: 'Vehicle Registration Plate',
      flex: 1,
      minWidth: 250
    },
    { field: 'Mobile', headerName: 'Mobile', flex: 1, minWidth: 200 },
    { field: 'Email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'Client', headerName: 'Client', flex: 1, minWidth: 200 },
    { field: 'Haulier', headerName: 'Haulier', flex: 1, minWidth: 200 },

    {
      field: 'Inducted',
      headerName: 'Inducted',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) =>
        params ? <CheckCircle color="success" /> : <Cancel color="error" />
    }
  ];

  const handleCreateContact = () => {
    navigate('/dashboard/drivers/new');
  };

  const handleRowClick = (params: any) => {
    navigate(`/dashboard/drivers/details/${params.row.Id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/dashboard/drivers/edit/${id}`);
  };

  const handleDeleteContact = async (id: number) => {
    try {
      await axios.delete(`/rest/api/drivers/${id}`);
      const newContacts = drivers.filter((el: any) => el.Id !== id);
      setDrivers(newContacts);
      enqueueSnackbar('Delete Success', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Delete Failed', { variant: 'success' });
    }
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    fetchDrivers(true);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    const updatedFilters = { ...filters, search: searchValue };
    setFilters(updatedFilters);

    // Debounce API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchDrivers(false, updatedFilters); // Pass the latest value directly
    }, 500); // Adjust debounce time as needed (500ms in this case)
  };

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Box
        sx={{
          padding: 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3
          }}
        >
          <HeaderBreadcrumbs
            heading="Drivers"
            links={[
              { name: 'Dashboard', href: '/dashboard' },
              { name: 'Drivers', href: '/dashboard/drivers' }
            ]}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 2, // Adds spacing between the buttons
              alignItems: 'center'
            }}
          >
            <Button
              variant="contained"
              onClick={handleCreateContact}
              startIcon={<EmojiTransportation />}
            >
              Create Driver
            </Button>
            <Tooltip title="Add Filters">
              <Button
                disableRipple
                color="inherit"
                variant="outlined"
                endIcon={<Icon icon={roundFilterList} />}
                onClick={() => setDrawerOpen(true)}
              >
                Filters
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <CustomPaper>
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="medium"
            sx={{ width: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GridSearchIcon />
                </InputAdornment>
              )
            }}
            value={filters.search}
            onChange={handleSearch}
          />

          <Box>
            <CommonTable
              rows={drivers}
              columns={columns}
              pageSize={filters.pageSize}
              totalRecords={totalRows}
              loading={loading}
              handlePageChange={(newPage) => setPage(newPage + 1)}
              handlePageSizeChange={(newPageSize) => {
                console.log(newPageSize, 'This is the new pages size');
                setPageSize(newPageSize);
                setFilters({ ...filters, pageSize: newPageSize });
              }}
              onEdit={handleEdit}
              onDelete={(id) => handleDeleteContact(id)}
              onRowClick={handleRowClick}
            />
          </Box>
        </CustomPaper>
        <DriverFiltersDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={() => {
            setDrawerOpen(false);
            fetchDrivers();
          }}
          onClear={clearFilters}
        />
      </Box>
    </Container>
  );
};

export default Drivers;
